import { useMemo, useEffect } from 'react';
import { pendingSavesActions, pendingSavesState } from '../store/pendingSavesStore';
import { resolveContextRoutes } from '../services/contextRouting';

/**
 * Options for resolving a component's pending-save id.
 */
interface UsePendingSavesOptions {
  /** Whether the component is inside a form context */
  insideForm: boolean;
  /** Whether the component is inside an entity context */
  insideEntity: boolean;
  /** Form document ID (required when insideForm is true) */
  formId?: string;
  /** Form version document ID (required when insideForm is true) */
  versionId?: string;
  /** Entity document ID (required when insideEntity is true) */
  entityId?: string;
  /** Entity record document ID (required when insideEntity is true) */
  recordId?: string;
  /** Unique identifier for this specific field or component within its context */
  identifier: string;
}

/**
 * Resolves the stable pending-save id for a field or component and guarantees
 * cleanup.
 *
 * The id is the component's Firestore document path plus its identifier. Callers
 * toggle pending state imperatively at the save lifecycle via
 * `pendingSavesActions.addPending(id)` / `removePending(id)`. This hook only
 * builds the id and registers an unmount / id-change cleanup so a component that
 * disappears mid-save never leaves a stale pending entry in the store.
 *
 * @example
 * ```tsx
 * const pendingId = usePendingSaves({
 *   insideForm: true,
 *   formId: 'form123',
 *   versionId: 'version456',
 *   insideEntity: false,
 *   identifier: 'field:projectName',
 * });
 * // on edit:   pendingSavesActions.addPending(pendingId);
 * // on settle: pendingSavesActions.removePending(pendingId);
 * ```
 */
export default function usePendingSaves({
  insideForm,
  insideEntity,
  formId,
  versionId,
  entityId,
  recordId,
  identifier,
}: UsePendingSavesOptions): string {
  const pendingId = useMemo(() => {
    const { path } = resolveContextRoutes({
      insideForm,
      insideEntity,
      formId,
      versionId,
      entityId,
      recordId
    });
    return `${path}#${identifier}`;
  }, [insideForm, insideEntity, formId, versionId, entityId, recordId, identifier]);

  useEffect(() => () => {
    pendingSavesActions.removePending(pendingId);
    pendingSavesState.failedSaveIds.delete(pendingId);
  }, [pendingId]);

  return pendingId;
}
